import { Injectable, Signal, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {CurrentUserAdminGQL, UserAdmin} from "../../../generated/graphql";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    // Perfis da equipe interna Linea. O back-office só opera com sessão de
    // equipe: token de dentista aqui significa cookie de refresh trocado entre
    // os dois portais, que dividem o jar de cookies do host da API.
    private static readonly TEAM_ROLES = [
        'admin', 'planner', 'factory', 'qa', 'logistic', 'delivered',
    ];

    private tokenKey = 'token';
    private platformId = inject(PLATFORM_ID);

    public BASE_URL = environment.base_url;

    token = signal<string | null>(this.getTokenFromStorage());

    private readonly _userSignal = signal<UserAdmin | null>(null);
    readonly userSignal = this._userSignal.asReadonly();

    private isFetchingUser = false;

    private refreshInFlight: Promise<string | null> | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private currentUserGQL: CurrentUserAdminGQL,
        private router: Router,
        private http: HttpClient
    ) {
        const existingToken = this.token();
        if (existingToken && this.isOtherAudienceToken(existingToken)) {
            // Sessão herdada do outro portal por refresh cruzado: descarta sem
            // navegar (o authGuard leva para /login). Seguir com ela deixaria a
            // tela "logada" e derrubaria o usuário na primeira query protegida.
            this.clearSession();
        } else if (existingToken) {
            this.scheduleRefresh(existingToken);
        }
        effect(() => {
            if (this.isAuthenticated() && !this.userSignal() && !this.isFetchingUser) {
                this.fetchCurrentUser();
            }
        });
    }

    login(token: string) {
        this.setToken(token);
        this.fetchCurrentUser();
    }

    /**
     * Renova a sessão usando o cookie httpOnly de refresh deste público
     * (a API mantém um cookie por público, equipe aqui).
     * Single-flight: chamadas concorrentes compartilham a mesma requisição.
     * Resolve com o novo access token, ou null se o refresh falhou.
     */
    refreshSession(): Promise<string | null> {
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }

        this.refreshInFlight = new Promise<string | null>(resolve => {
            this.http.post<any>(environment.base_graphql, {
                operationName: 'refreshToken',
                query: 'mutation refreshToken { refreshToken { token } }'
            }, { withCredentials: true }).subscribe({
                next: res => {
                    const token = res?.data?.refreshToken?.token;
                    if (token && !res?.errors?.length) {
                        if (this.isOtherAudienceToken(token)) {
                            // Renovação devolveu a sessão do outro portal: não
                            // assumir essa identidade — quem chamou desloga.
                            resolve(null);
                            return;
                        }
                        this.setToken(token);
                        resolve(token);
                    } else {
                        resolve(null);
                    }
                },
                error: () => resolve(null)
            });
        }).finally(() => {
            this.refreshInFlight = null;
        });

        return this.refreshInFlight;
    }

    logout() {
        this.http.post(environment.base_graphql, { query: 'mutation { logout }' }, { withCredentials: true }).subscribe({
            next: () => {},
            error: () => {}
        });
        this.clearSession();
        if (isPlatformBrowser(this.platformId)) {
            this.router.navigate(['/login']);
        }
    }

    // Zera a sessão local, sem navegar e sem avisar o servidor.
    private clearSession() {
        this.token.set(null);
        this._userSignal.set(null);
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (isPlatformBrowser(this.platformId)) {
            sessionStorage.removeItem(this.tokenKey);
            localStorage.removeItem(this.tokenKey);
        }
    }

    private setToken(token: string) {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.tokenKey, token);
            sessionStorage.removeItem(this.tokenKey);
        }
        this.token.set(token);
        this.scheduleRefresh(token);
    }

    // Refresh proativo: renova 60s antes do access token expirar
    private scheduleRefresh(token: string) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        const exp = this.decodeTokenPayload(token)?.exp;
        if (typeof exp !== 'number') {
            return;
        }
        const delay = Math.max(exp * 1000 - Date.now() - 60_000, 5_000);
        this.refreshTimer = setTimeout(() => this.refreshSession(), delay);
    }

    private decodeTokenPayload(token: string): { exp?: number; role?: string } | null {
        try {
            // payload de JWT é base64url: sem converter, atob quebra em token
            // que contenha '-' ou '_'
            const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
            return JSON.parse(atob(padded));
        } catch {
            return null;
        }
    }

    /**
     * true quando o token pertence ao outro público (dentista). Token que não dá
     * para decodificar devolve false de propósito: na dúvida, não descartar
     * sessão possivelmente válida.
     */
    private isOtherAudienceToken(token: string): boolean {
        const role = this.decodeTokenPayload(token)?.role;
        return !!role && !AuthService.TEAM_ROLES.includes(role);
    }

    isAuthenticated(): boolean {
        return !!this.token();
    }

    fetchCurrentUser() {
        if (!this.isAuthenticated() || this.isFetchingUser) return;

        this.isFetchingUser = true;

        this.currentUserGQL.watch().valueChanges.subscribe(
            ({ data }) => {
                if (data?.currentUserAdmin) {
                    this.updateUser(data.currentUserAdmin as UserAdmin);
                }
                this.isFetchingUser = false;
            },
            error => {
                console.warn('Erro ao buscar usuário (Sessão mantida):', error);
                // Não deslogar agressivamente aqui para evitar bugs de navegação
                // Apenas marcar que não está mais buscando
                this.isFetchingUser = false;
            }
        );
    }

    updateUser(user: UserAdmin) {
        this._userSignal.set(user);
    }

    getUserSignal(): Signal<UserAdmin | null> {
        return this.userSignal;
    }

    private getTokenFromStorage(): string | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const stored = localStorage.getItem(this.tokenKey);
        if (stored) {
            return stored;
        }

        // migração: sessões antigas guardavam o token em sessionStorage
        const legacy = sessionStorage.getItem(this.tokenKey);
        if (legacy) {
            localStorage.setItem(this.tokenKey, legacy);
            sessionStorage.removeItem(this.tokenKey);
        }
        return legacy;
    }

    // upload() removido: era uma duplicata morta (zero call sites) do
    // UtilComponent.upload — o caminho atual é o UploadService.
}
